import * as React from 'react';

import { cn } from '@mint-lib/tw-utils';
import { cva } from 'class-variance-authority';
import { Tabs as TabsPrimitive } from 'radix-ui';

import { Button } from './button.jsx';
import { typographyVariants } from './typography.jsx';

const tabTriggerVariants = cva(
  'group/tabs relative inline-flex cursor-pointer items-center rounded-sm bg-transparent-main fill-text-secondary text-start after:absolute hover:bg-ui-hover focus-visible:outline-2 focus-visible:outline-action-focusBorder disabled:pointer-events-none disabled:fill-action-disabled01 disabled:text-action-disabled01 data-[orientation=horizontal]:after:inset-x-1 data-[orientation=horizontal]:after:-bottom-1.25 data-[orientation=horizontal]:after:h-0.5 data-[orientation=vertical]:justify-start data-[orientation=vertical]:after:-right-1.25 data-[orientation=vertical]:after:h-full data-[orientation=vertical]:after:w-0.5 data-[state=active]:bg-background-tertiary data-[state=active]:fill-tertiary-dark data-[state=active]:font-medium data-[state=active]:text-tertiary-dark data-[state=active]:after:bg-tertiary-main data-[state=active]:hover:bg-ui-highlight',
  {
    variants: {
      size: {
        medium: 'h-8 px-3 py-1',
        large: 'h-10 px-4 py-0',
      },
    },
  },
);

function Tabs({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Root>) {
  return (
    <TabsPrimitive.Root data-slot="tabs" className={cn(className)} {...props} />
  );
}

function TabsList({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List>) {
  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      className={cn(
        'flex w-fit border-b-border-secondary data-[orientation=horizontal]:flex-row data-[orientation=horizontal]:border-b data-[orientation=horizontal]:pb-1 data-[orientation=vertical]:flex-col data-[orientation=vertical]:border-r data-[orientation=vertical]:border-r-border-secondary data-[orientation=vertical]:pr-1',
        className,
      )}
      {...props}
    />
  );
}

function TabsTrigger({
  className,
  size = 'medium',
  children,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger> & {
  size?: 'medium' | 'large';
}) {
  // Count the number of children (excluding null, undefined, and empty strings)
  const childCount = React.Children.count(children);
  const hasMultipleChildren = childCount > 1;

  // Add gap class when there are multiple children
  const gapClass = hasMultipleChildren ? 'gap-2' : '';

  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(
        tabTriggerVariants({ size }),
        typographyVariants({ variant: 'bodyShort01', color: 'secondary' }),
        gapClass,
        className,
      )}
      {...props}
    >
      {children}
    </TabsPrimitive.Trigger>
  );
}
function TabsActionButton({
  children,
  className,
  ...props
}: React.ComponentProps<typeof Button>) {
  return (
    <Button
      icon
      variant="plain"
      size="small"
      className={cn(
        'group-data-[state=active]/tabs:opacity-100 group-data-[state=inactive]/tabs:hidden',
        className,
      )}
      {...props}
    >
      {children}
    </Button>
  );
}

function TabsContent({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn(className, 'focus:outline-none')}
      {...props}
    />
  );
}

export { Tabs, TabsList, TabsTrigger, TabsContent, TabsActionButton };
